/**
 * Which screens each app of the rail lists, in what order — ADR 9023 and DEC-89's common entries —
 * and what each app reaches, which the Documentation screen reads (ADR 9035).
 *
 * It lived in `src/app/chrome/rail-layout.ts` until the Documentation screen needed it: a feature
 * may not import the shell (G11 dependency direction), and the shell may import a feature, so the
 * list sits here, beside the manifest contract and the registry, and the rail re-exports it. It is
 * still ONE list in one place; `rail-layout.ts` holds how the rail draws it, and its header the
 * reasoning behind the list.
 *
 * No runtime imports but `documentation-sections.ts` and `parameter-families.ts`, which have none:
 * the chrome is in every page's first bundle and must not pull a screen with it.
 */

import type { NavEntry } from './manifest';
import type { ChapterNav } from './registry';
import { DOCUMENTATION_PATH, DOCUMENTATION_SECTION_PARAM } from './foundations/documentation-sections';
import { PARAMETER_FAMILIES, type ParameterFamilyId } from './parameter-families';

/**
 * Simulate's path and its stage key, spelled here rather than imported: the module that
 * declares them (`src/features/inseason/simulate.ts`) pulls the API client and the charts
 * into whatever imports it, and the chrome is in every page's first bundle.
 * `src/app/chrome/rail-layout.test.ts` asserts they still equal ch04's constants.
 */
export const SIMULATE_PATH = '/inseason/simulate';
export const SIMULATE_STAGE_PARAM = 'stage';
/** Simulate's history, its composer's other tab — ch04's `SIMULATE_HISTORY_PATH`, spelled here for the same reason. */
export const SIMULATE_HISTORY_PATH = `${SIMULATE_PATH}/history`;
/**
 * Compacting › Simulazione and its history, its other tab — ch04's `COMPACTING_SIMULATION_PATH` and
 * `COMPACTING_SIMULATION_HISTORY_PATH`, spelled here for the same reason.
 */
export const COMPACTING_SIMULATION_PATH = '/inseason/compacting-simulation';
export const COMPACTING_SIMULATION_HISTORY_PATH = `${COMPACTING_SIMULATION_PATH}/history`;
/**
 * «Tutte le celle dell'esecuzione», Proposals' other tab — ch04's `ALLOCATION_CELLS_PATH`, spelled here
 * for the same reason.
 */
export const ALLOCATION_CELLS_PATH = '/inseason/allocation-cells';
/** «Perché»'s «Non inviate», its other tab — ch04's `ALLOCATION_NOT_SENT_PATH`, spelled here for the same reason. */
export const ALLOCATION_NOT_SENT_PATH = '/inseason/allocation-why-not-sent';

/**
 * The parameter register and the key of its app tab (ADR 9025), spelled here for the same reason
 * as Simulate's: the module that declares them (`src/features/foundations/parameters-v7/`) is not
 * the chrome's to import. `src/app/chrome/rail-layout.test.ts` asserts they still equal ch00's constants.
 */
export const PARAMETERS_PATH = '/foundations/parameters';
export const PARAMETERS_APP_PARAM = 'app';
/** The register's key-family filter (`docs/adr/9064`) — ch00's `KEYS_PARAM`, spelled here for the same reason. */
export const PARAMETERS_KEYS_PARAM = 'keys';
/** The parameters' pending approvals as a page (DEC-89) — ch00's route, spelled here for the same reason. */
export const PARAMETER_APPROVALS_PATH = '/foundations/parameters/approvals';

/** The query a family's «Parametri» sub-entry opens the register with: its app and its key prefix. */
export function parameterFamilyQuery(id: ParameterFamilyId): Readonly<Record<string, string>> {
  const family = PARAMETER_FAMILIES[id];
  return { [PARAMETERS_APP_PARAM]: family.app, [PARAMETERS_KEYS_PARAM]: family.prefix };
}

export const RAIL_APP_IDS = [
  'pricing', 'promotions', 'replenishment', 'compacting', 'health', 'management',
  // The two apps drawn inside «Próximas ampliaciones», usable (`placement: 'future'`, `docs/adr/9082`).
  'initialAllocation', 'assortment',
] as const;
export type RailAppId = (typeof RAIL_APP_IDS)[number];

/**
 * Where the rail draws an app — `docs/adr/9082`. `'main'` (the default) is a group of its own under the
 * «App» kicker; `'future'` is inside «Próximas ampliaciones» (`FUTURE_GROUP`), under a plain heading of
 * its own, ABOVE the inert rows. A placement, not a state: the app's entries are enabled and routed
 * whichever it is, and `railGroup` on a manifest (a chapter's section) is a different thing the layout
 * has never read.
 */
export const RAIL_APP_PLACEMENTS = ['main', 'future'] as const;
export type RailAppPlacement = (typeof RAIL_APP_PLACEMENTS)[number];

export interface RailAppEntry {
  /** The manifest navigation entry's `to`, exactly — or, with `view`, one of its children's. */
  readonly to: string;
  /**
   * `to` names a manifest CHILD (a view), drawn as an entry of its own with the view's label rather
   * than indented under its parent: the Replenishment menu has no second level (owner ruling
   * 2026-10-24, `docs/adr/9023`). The manifest keeps its `children`; only this rail flattens them.
   * `rail-layout.test.ts` fails when no manifest declares the view, or declares it disabled.
   */
  readonly view?: true;
  /** The query the entry opens with. Absent for a plain link. */
  readonly query?: Readonly<Record<string, string>>;
  /**
   * `'manifest'`: draw the manifest entry's declared `children` as sub-entries, indented under
   * it. Absent: a plain entry. `rail-layout.test.ts` fails when an entry asks for sub-entries
   * its manifest does not declare, or declares disabled.
   */
  readonly subEntries?: 'manifest';
  /**
   * The paths of the entry's other parts, which its page offers as tabs of its own instead of the
   * rail drawing them (owner ruling 2026-10-24, `docs/adr/9023`): Simulation's history, Proposals'
   * «Tutte le celle dell'esecuzione». Not drawn; the entry is marked while the planner is on one,
   * and Documentation counts them as reached. `rail-layout.test.ts` fails on a path no route has.
   */
  readonly tabs?: readonly string[];
  /**
   * A «Parametri» sub-entry drawn LAST under the manifest's children: the register opened on the
   * app and on this key family (`?app=&keys=`, `docs/adr/9064`). Only with `subEntries: 'manifest'`.
   */
  readonly parameters?: ParameterFamilyId;
  /** The rail's own name for the entry, where it is not the manifest's (a common entry). */
  readonly labelKey?: string;
  /** The first of the app's common entries: a rule is drawn above it (brief part 2 §1). */
  readonly common?: true;
  /**
   * A rule drawn above an entry that is not the first common one: Replenishment's second group,
   * which opens on «Servizio e scorta» (owner ruling 2026-10-24, `docs/adr/9023`). Drawn as
   * `common` is; an app that sets it sets it once and marks no common entry.
   */
  readonly rule?: true;
}

export interface RailAppDefinition {
  readonly id: RailAppId;
  /** Rail group labels live in the shell's catalogue (`core.json`); screen labels in their feature's. */
  readonly labelKey: string;
  /** Where the rail draws the app (`RAIL_APP_PLACEMENTS`); absent is `'main'`. */
  readonly placement?: RailAppPlacement;
  readonly entries: readonly RailAppEntry[];
}

/**
 * An app's common entries, in the one order (DEC-89, brief part 2 §1): Parametri · Approvazioni in
 * attesa · Documentazione. `approvals` is the app's decision queue; `null` opens the parameters'
 * own queue on the app.
 */
function commonEntries(app: RailAppId, approvals: RailAppEntry | null): readonly RailAppEntry[] {
  const management = app === 'management';
  return [
    {
      to: PARAMETERS_PATH,
      query: { [PARAMETERS_APP_PARAM]: app },
      labelKey: management ? 'nav.common.generalParameters' : 'nav.common.parameters',
      common: true,
      // Management's «Parametri generali» is a group: Registro · Scadenze e storico (bata-ui#196 §1).
      ...(management ? { subEntries: 'manifest' as const } : {}),
    },
    { labelKey: 'nav.common.approvals', ...(approvals ?? { to: PARAMETER_APPROVALS_PATH, query: { [PARAMETERS_APP_PARAM]: app } }) },
    { to: DOCUMENTATION_PATH, query: { [DOCUMENTATION_SECTION_PARAM]: app } },
  ];
}

/** An entry without the rule `common` draws above it: a common entry placed among the app's own. */
function unruled(entry: RailAppEntry): RailAppEntry {
  const { common, ...rest } = entry;
  void common;
  return rest;
}

/**
 * Replenishment's common entries, split between its two groups (owner ruling 2026-10-24): «Parametri»
 * closes the first, unruled; «Approvazioni in attesa» and Documentazione close the second.
 */
const [REPLENISHMENT_PARAMETERS, REPLENISHMENT_APPROVALS, REPLENISHMENT_DOCUMENTATION] =
  commonEntries('replenishment', { to: '/inseason/allocation-decisions' }) as readonly [RailAppEntry, RailAppEntry, RailAppEntry];

export const RAIL_APPS: readonly RailAppDefinition[] = [
  {
    id: 'pricing',
    labelKey: 'nav.app.pricing',
    entries: [
      // Every entry with pages under it draws them as sub-entries (DEC-84, client rule 1), and each
      // page does one thing (client rule 3, DEC-88; the client-rules audit §4). The sub-entries are
      // the chapters' manifest children, not re-typed here: Price & markdown ▸ Run · Output ·
      // Frontier · Stock position · Explanation · Saved configurations (issue 226); Cadence ▸ Queue · Required rate · Override
      // history; Exit plans ▸ Register · Unsigned · Terminal check · Terminal position; Simulate ▸
      // New simulation · History (with `?stage=price`, which its sub-entries carry).
      { to: '/inseason/markdown', subEntries: 'manifest' },
      { to: '/exit/cadence', subEntries: 'manifest' },
      { to: '/exit/plans', subEntries: 'manifest' },
      // «Estate exit position» ▸ Position · Stranded lots (owner ruling 2026-11-04, bata-platform#951): from
      // Compacting, beside the plans its position is measured against. Its route did not change.
      { to: '/exit/position', subEntries: 'manifest' },
      // End of life (bata-reqs issue 35, ch05 ADR 7003–7005), with no «Fine vita» level since
      // issue 38 R1 (owner rulings 2026-10-17, `docs/adr/9064`): two entries, each its pages (ch05's
      // manifest children) and its own «Parametri» — the register on the family's keys, which
      // Pricing's general «Parametri» no longer shows — then the phase register as an entry of
      // its own. Transition to end of life ▸ Recommendations · Withdrawal · Parametri; Exit to
      // outlet ▸ Recommendations · Transfer to outlet · Parametri. The moves' lane-book lines are
      // decided on their own pages (ADR 7005).
      { to: '/exit/lifecycle', subEntries: 'manifest', parameters: 'lifecycleTransition' },
      { to: '/exit/lifecycle-outlet', subEntries: 'manifest', parameters: 'outletExit' },
      { to: '/exit/lifecycle-phases' },
      // bata-reqs issue 38 R2 (ADR 9068): the market's process calendar — the periods the transition,
      // the outlet exit and the price steps take effect in, after the phase register. ch00's route.
      { to: '/foundations/process-calendar' },
      // Promotions and Campaign simulation left Pricing for their own group (ADR 9055, owner
      // ruling 2026-10-09): see «Promozioni e campagne» below.
      { to: SIMULATE_PATH, query: { [SIMULATE_STAGE_PARAM]: 'price' }, subEntries: 'manifest' },
      // The common entries. Parametri: the registry rows a price solve is posed with (they left
      // the Run page, client rule 3). Approvazioni in attesa: the run's price decisions (the
      // client-rules audit §4); cadence decisions stay on the cadence queue's rows, beside the
      // numbers they are taken on. It is a GROUP (issue 226, owner ruling 2026-10-07): the
      // decisions, then the run's calendar of the same steps — ch04's manifest children — so
      // Price & markdown keeps six sub-entries. R4's per-app "Report" was dropped (owner ruling
      // 2026-10-03, ADR 9026 released): the enriched proposal and transfer files carry it.
      ...commonEntries('pricing', { to: '/inseason/markdown/approvals', subEntries: 'manifest' }),
    ],
  },
  {
    // «Promozioni e campagne» — ADR 9055 (owner ruling 2026-10-09, demo request 4): what past
    // promotions did and what a planned campaign would do are one subject, so they sit together
    // in one group rather than among Pricing's price steps. Only the MENU moved: the routes stay
    // in their chapters (ch06 `/postseason/promotions`, ch04 `/inseason/campaigns`) and no URL
    // changed. Its common entries are «Parametri» and Documentazione (ADR 9055 as amended, owner
    // rulings 2026-10-17 second set): «Parametri» is the register opened on the campaign family
    // (`?app=management&keys=campaign.`), whose keys left Management's «Parametri generali». No
    // «Approvazioni in attesa»: a campaign simulation is advisory and decides nothing, and the
    // family's proposals wait in the register's own queue under the table.
    id: 'promotions',
    labelKey: 'nav.app.promotions',
    entries: [
      // Issue 314 (ADR 8004): what past promotion windows did — Windows · By mechanic, ch06's
      // manifest children.
      { to: '/postseason/promotions', subEntries: 'manifest' },
      // bata-reqs#33 (ch04 ADR 6017): advisory campaign simulation — New campaign · History, ch04's
      // manifest children. Beside Promotions, whose response fit it is built on.
      { to: '/inseason/campaigns', subEntries: 'manifest' },
      { to: PARAMETERS_PATH, query: parameterFamilyQuery('campaigns'), labelKey: 'nav.common.parameters', common: true },
      { to: DOCUMENTATION_PATH, query: { [DOCUMENTATION_SECTION_PARAM]: 'promotions' } },
    ],
  },
  {
    id: 'replenishment',
    labelKey: 'nav.app.replenishment',
    entries: [
      // ONE level, two groups under a rule (owner ruling 2026-10-24, `docs/adr/9023`): what the
      // planner works the run with, then what they check it against. No entry draws sub-entries:
      // the pages that were «Allocazione e riassortimento» and «Vendite perse» views are entries of
      // their own (`view`), named as their views were, and the manifests keep their `children` for
      // the other apps and the palette. Simulation's two pages are one entry, and the page carries
      // «Nuova simulazione · Storico» as its own tabs (`simulate-parts.tsx`).
      //
      // Group 1. Proposals: the run's lines, the first allocation view; every cell of the run is its
      // other part, a tab of the page.
      { to: '/inseason/allocation', view: true, tabs: [ALLOCATION_CELLS_PATH] },
      // «Parametri», the app's common entry, moved up beside the proposals it poses: unruled.
      unruled(REPLENISHMENT_PARAMETERS),
      // Simulation with the distribute stage, one entry; New simulation · History are its page's tabs.
      { to: SIMULATE_PATH, query: { [SIMULATE_STAGE_PARAM]: 'distribute' }, tabs: [SIMULATE_HISTORY_PATH] },
      { to: '/inseason/allocation-why', view: true, tabs: [ALLOCATION_NOT_SENT_PATH] },
      // Post-season's census of lost sales (bata-ui#196 §7), as «Vendite perse»: the manifest
      // entry's own label, opening its first view, the census.
      { to: '/postseason/lost-sales' },
      // Group 2, under the rule. Issue 308 (ADR 6015): the service exchange curve, what each
      // stocking level of the forecast would have cost in pairs and served. «Salute della rete» left
      // this group for the Health app (bata-reqs ADR 9003, `docs/adr/9079`); the group still opens here.
      { to: '/inseason/service-exchange', rule: true },
      { to: '/inseason/allocation-stock', view: true },
      { to: '/postseason/lost-sales-potential', view: true },
      { to: '/postseason/lost-sales-panel', view: true },
      // The approvals are the decision on the run's lines, which stood at the foot of the
      // allocation screen. R4: Report. See Pricing.
      REPLENISHMENT_APPROVALS,
      REPLENISHMENT_DOCUMENTATION,
    ],
  },
  {
    id: 'compacting',
    labelKey: 'nav.app.compacting',
    entries: [
      // The one run's report first (bata-reqs ADR 9002 stage 3): what the day's run decided, per article
      // state. Owner ruling 2026-11-02: the history of the engines it replaced is no entry — «Stock
      // transfers» (Transfers · Why) and Clearance consolidation's Lanes and Simulate — and only what
      // still reads live stays: Clearance consolidation ▸ Estate · Doors · Lane what-if (the position
      // the consolidation jobs still compute, and the lane calculator), Cost to serve (gate D2, which
      // the one run reads), Estate exit position ▸ Position · Stranded lots. ch05's manifest children,
      // not re-typed here. The hidden pages keep their routes for the links that name a run of them.
      { to: '/inseason/compacting-report' },
      // «Simulazione» (owner rulings 2026-11-02, platform ADR 9056): its own page, beside the report it used to
      // be a button on. ONE entry; New simulation · History are its page's tabs, as Replenishment's.
      { to: COMPACTING_SIMULATION_PATH, tabs: [COMPACTING_SIMULATION_HISTORY_PATH] },
      // bata-platform#951 (owner rulings 2026-11-03/04): the menu is flat. «Consolidation» and «Lanes» are the
      // one run's, a page each beside its report and simulation; the Estate and Doors addresses open
      // Consolidation, and Lane what-if is no entry (its route stays for the links that name it). «Estate exit
      // position» moved to Pricing, beside Exit plans: it reads nothing of the move run.
      { to: '/inseason/compacting-consolidation' },
      { to: '/inseason/compacting-lanes' },
      { to: '/inseason/cost-to-serve' },
      // The common entries. Approvazioni in attesa: the one run's merged list (stage 3 rulings 1 and 2) —
      // every store-to-store move decided in one approval process; the transfer queue and the lane book's
      // decisions were retired with the legacy writers (ruling 7). R4: Report. See Pricing.
      ...commonEntries('compacting', { to: '/inseason/compacting-moves' }),
    ],
  },
  {
    // «Salute» — bata-reqs ADR 9003 (owner rulings 2026-10-31 Q1/Q1b/Q2 and 2026-11-01 O5),
    // `docs/adr/9079`: management's reports, each a bird's-eye view linked to the queues that act on
    // it. The network-health cockpit moved here whole — levels 0–3 and Doors, which match its entry by
    // prefix — and its URLs did not change. Read-only: nothing is decided in Health, so it has no
    // «Approvazioni in attesa» (the ADR 9055 exception, as Promotions). «Parametri» is the register
    // opened on the management targets (`?app=management&keys=health.target.`), which leave
    // Management's «Parametri generali».
    id: 'health',
    labelKey: 'nav.app.health',
    entries: [
      // The four reports (ch01, `docs/adr/3003`), with the cockpit second, beside Availability whose cells it
      // breaks down (docs/25 §5.4 B1's order).
      { to: '/strategic/health/availability' },
      { to: '/inseason/network-health' },
      { to: '/strategic/health/coverage' },
      { to: '/strategic/health/sell-through' },
      { to: '/strategic/health/markdown-exposure' },
      // «Ricerca» (ch01, `docs/adr/3004`): the client's spot check of our stock and sales against their own
      // system — the last of the reports, above the rule.
      { to: '/strategic/health/lookup' },
      // Health's «Parametri» are the management targets, so the entry is named for them — «Obiettivi» /
      // «Targets» / «Metas» (`nav.app.health.targets`, ADR 9079 amended 2026-11-02); every other app keeps
      // «Parametri».
      { to: PARAMETERS_PATH, query: parameterFamilyQuery('health'), labelKey: 'nav.app.health.targets', common: true },
      { to: DOCUMENTATION_PATH, query: { [DOCUMENTATION_SECTION_PARAM]: 'health' } },
    ],
  },
  {
    id: 'management',
    labelKey: 'nav.app.management',
    entries: [
      // Package A (2026-09-30, client rules 1 and 3): a page that held two or three functions is
      // one page per function now, drawn as its sub-entries — Workbench ▸ Alerts · Proposals;
      // Write-back ▸ Delivery log · Execution pack · Backlog; Impact ▸ By module · Benefits ·
      // Override outcomes; Test vs control ▸ D4 pairs · Matched control; Data ▸ Feeds ·
      // Schedules; and Cycles ▸ Status · Findings · Trend, off the Cockpit (DEC-87). The parent's
      // URL is still its first page.
      { to: '/inseason/workbench', subEntries: 'manifest' },
      { to: '/inseason/write-back', subEntries: 'manifest' },
      // DEC-87: the Cockpit's cycle status, findings and trend, as Management pages.
      { to: '/inseason/cycles', subEntries: 'manifest' },
      // What one run read (ADR 6121): the run's, across stages, so Management's (bata-ui#215).
      { to: '/inseason/run-provenance' },
      // «Perimetri» (bata-reqs ADR 1003, ADR 6028): which stores × articles a run covers, per market.
      { to: '/inseason/run-scopes' },
      { to: '/strategic/impact', subEntries: 'manifest' },
      { to: '/strategic/test-vs-control', subEntries: 'manifest' },
      // bata-ui#166 W3-7 (#178, #189): the monthly management pack is produced every month.
      { to: '/strategic/monthly-pack' },
      // bata-ui#166 W1-1/W1-2/W1-7 (ADR 2006): what ran, on which data, and whether a module is
      // paused must be one click away — the Cockpit's band links here too.
      { to: '/foundations/feeds', subEntries: 'manifest' },
      { to: '/foundations/modules' },
      { to: '/foundations/autonomy' },
      // bata-ui#166 W3-3/W3-5 (#184, #188): the alert queue and the rules that raise it are
      // worked daily — My work links to the queue — so they are not "future" (bata-ui#196 §7).
      { to: '/foundations/exceptions' },
      { to: '/foundations/exception-rules' },
      { to: '/postseason/review' },
      // bata-ui#218 (ADR 9037): «Registro attività», who did what and when — the last of Management's
      // own entries, before the common ones. Withheld from a person the platform denies `activity ·
      // read` (`rail-capabilities.ts`).
      { to: '/foundations/activity' },
      // The common entries: «Parametri generali» (bata-ui#196 §1) — the keys no other app owns,
      // `?app=management` — and the parameters' queue on them.
      ...commonEntries('management', null),
    ],
  },
  {
    // «Asignación inicial» — bata-platform#939 §8, `docs/adr/9082`, `docs/adr/5001`: the first send of an article that has
    // just reached the distribution centre, to the stores that should carry it. DRAWN INSIDE «Próximas
    // ampliaciones» (`placement: 'future'`), USABLE: its routes are the launch chapter's, and the chapter's
    // other three pages (`plan`, `size-runs`, `withholding`) stay disabled and are not drawn beside it
    // (`railLayout`: no inert siblings). One entry per page (R6): the articles to allocate, the simulation, the
    // runs, the lines and why, and the approvals — «Aprobaciones pendientes», the app's decision queue. Its
    // «Parámetros» closes the group above «Documentación»: the register opened on the app (`?app=initialAllocation`), which holds the
    // nine keys the platform declares for it (`launch.initial.*`; ADR 9025 amended 2026-11-03). Its parameter sets wait in the register's
    // own queue under the table, as the other apps' do.
    id: 'initialAllocation',
    labelKey: 'nav.app.initialAllocation',
    placement: 'future',
    entries: [
      { to: '/launch/initial' },
      { to: '/launch/initial/simulate' },
      { to: '/launch/initial/runs' },
      { to: '/launch/initial/lines' },
      { to: '/launch/initial/approvals', labelKey: 'nav.common.approvals' },
      { to: PARAMETERS_PATH, query: { [PARAMETERS_APP_PARAM]: 'initialAllocation' }, labelKey: 'nav.common.parameters', common: true },
      { to: DOCUMENTATION_PATH, query: { [DOCUMENTATION_SECTION_PARAM]: 'initialAllocation' } },
    ],
  },
  {
    // «Surtido» — bata-platform#940 §8, `docs/adr/9082`: breadth and depth per store and category, and the
    // plan that closes the gap. The same placement as «Asignación inicial»; its routes are the pre-season
    // chapter's (the census admits the seven chapter slugs), and that chapter's four pages stay disabled
    // and undrawn beside it. Its «Parámetros» is the register opened on the app (`?app=assortment`), the seven keys the platform
    // declares for it (`assortment.*`; ADR 9025 amended 2026-11-03). It has no «Aprobaciones pendientes» of its own: a plan's rows are
    // decided on its pages (`docs/adr/4001`).
    id: 'assortment',
    labelKey: 'nav.app.assortment',
    placement: 'future',
    entries: [
      // Four pages, one function each (ADR 9070 R6; `docs/adr/4001`): the matrix, one store, the plan with its
      // «Por qué», and the store groups.
      { to: '/preseason/assortment' },
      { to: '/preseason/assortment/store' },
      { to: '/preseason/assortment/plan' },
      { to: '/preseason/assortment/bands' },
      { to: PARAMETERS_PATH, query: { [PARAMETERS_APP_PARAM]: 'assortment' }, labelKey: 'nav.common.parameters', common: true },
      { to: DOCUMENTATION_PATH, query: { [DOCUMENTATION_SECTION_PARAM]: 'assortment' } },
    ],
  },
];

/**
 * The paths an app's rail entries reach: each entry's own, for an entry drawn with
 * `subEntries: 'manifest'` its usable manifest children, and the `tabs` its page offers — what the
 * rail offers, derived from the same list rather than kept twice. A path no manifest offers, or
 * offers disabled, is not reached. The Documentation screen reads it to show an app only the screen
 * guides its rail reaches (ADR 9035).
 */
export function appReach(
  chapters: readonly ChapterNav[],
  app: RailAppId,
  apps: readonly RailAppDefinition[] = RAIL_APPS,
): readonly string[] {
  const byPath = new Map(chapters.flatMap((chapter) => chapter.entries).map((entry) => [entry.to, entry]));
  const views = manifestViews(chapters);
  const reached = (apps.find((each) => each.id === app)?.entries ?? []).flatMap(({ to, subEntries, parameters, view, tabs = [] }) => {
    const entry = view === true ? views.get(to) : byPath.get(to);
    if (entry === undefined || entry.disabled === true) return [];
    const children = subEntries === 'manifest'
      ? [...(entry.children ?? [])].sort((a, b) => a.order - b.order).filter((child) => child.disabled !== true).map((child) => child.to)
      : [];
    const register = subEntries === 'manifest' && parameters !== undefined && byPath.has(PARAMETERS_PATH) ? [PARAMETERS_PATH] : [];
    return [to, ...children, ...tabs, ...register];
  });
  return [...new Set(reached)];
}

/**
 * Every usable manifest child (a view) by its path, for an entry drawn with `view`: the first that
 * declares it wins, and a view that is disabled, or whose parent is, is left out.
 */
export function manifestViews(chapters: readonly ChapterNav[]): ReadonlyMap<string, NavEntry> {
  const views = new Map<string, NavEntry>();
  for (const entry of chapters.flatMap((chapter) => chapter.entries)) {
    if (entry.disabled === true) continue;
    for (const child of entry.children ?? []) if (child.disabled !== true && !views.has(child.to)) views.set(child.to, child);
  }
  return views;
}
